import { ProviderSearchContext } from "context/ProviderSearchContext";
import {
  GLOSSARY_SECTION_CONSULTANTS,
  PROVIDER_SEARCH_STATE_MAP,
} from "core/consts";
import { pathToUrl } from "core/model/utils/urls";
import { GlossarySection } from "core/types";
import { ProviderSearchFilters } from "dsl/organisms/Filters/ProviderSearchApp/types";
import { USER_FILTER_TREATMENT_LOCATION } from "dsl/organisms/Filters/consts";
import toFilterQueryString from "dsl/organisms/Filters/utils/toQueryString";
import { Location, NavigateFunction } from "react-router-dom";

export const getProviderSearchNavigationUrls = ({
  location,
  providerSearchContext,
}: {
  location: Location;
  providerSearchContext: ProviderSearchContext;
}) => {
  const { search } = location;
  const { providerSearch } = providerSearchContext;
  const state = PROVIDER_SEARCH_STATE_MAP[providerSearch];

  const urls = {
    goToAccount({ accountId }: { accountId: number }) {
      return pathToUrl({
        pathname: `/providersearch/${state}/account/${accountId}`,
        search,
      });
    },
    goToConsultant({ consultantId }: { consultantId: number }) {
      return pathToUrl({
        pathname: `/providersearch/${state}/consultant/${consultantId}`,
        search,
      });
    },
    goToConsultantPrint({ consultantId }: { consultantId: number }) {
      return pathToUrl({
        pathname: `/providersearch/${state}/print-consultant/${consultantId}`,
        search: search ? `${search}&print` : "print",
      });
    },
    goToGlossary(
      section: GlossarySection | undefined = GLOSSARY_SECTION_CONSULTANTS,
    ) {
      return pathToUrl({
        pathname: `/providersearch/${state}/glossary/${section}`,
        search,
      });
    },
    goToListing() {
      return pathToUrl({
        pathname: `/providersearch/${state}/listing`,
        search,
      });
    },
    goToListingPrint({ filters }: { filters: ProviderSearchFilters }) {
      const query = `?${toFilterQueryString(filters)}&print`;
      return pathToUrl({
        pathname: `/providersearch/${state}/printable-listing`,
        search: query,
      });
    },
    goToOnboardingFunnel(searchString?: string) {
      return pathToUrl({
        pathname: `/providersearch/${state}`,
        search: searchString ?? "",
      });
    },
    goToProvider({
      careproviderId,
      zipcode,
    }: {
      careproviderId: number;
      zipcode?: string;
    }) {
      const searchString = zipcode
        ? `${search}${
            search.includes("?") ? "&" : "?"
          }${USER_FILTER_TREATMENT_LOCATION}=${zipcode}`
        : search;

      return pathToUrl({
        pathname: `/providersearch/${state}/provider/${careproviderId}`,
        search: searchString,
      });
    },
    goToProviderPrint({ careproviderId }: { careproviderId: number }) {
      return pathToUrl({
        pathname: `/providersearch/${state}/print-provider/${careproviderId}`,
        search: search ? `${search}&print` : "print",
      });
    },
    goToRequest(id: number, tabName?: string) {
      return pathToUrl({
        pathname: `/providersearch/${state}/request/${id}`,
        search: tabName ? `${search}&activeTab=${tabName}` : search,
      });
    },
    goToSearchDashboard(id?: number) {
      let patientId = id;
      // if no patient is passed to travel to the first available patient id
      if (!patientId) {
        patientId =
          providerSearchContext.patients.active?.[0]?.id ??
          providerSearchContext.patients.archived?.[0]?.id;
      }

      return pathToUrl({
        pathname: `/providersearch/${state}/dashboard/patient/${patientId}`,
        search,
      });
    },
    goToTableOfContents() {
      return pathToUrl({
        pathname: `/providersearch/${state}/tableofcontents`,
        search,
      });
    },
  };

  return urls;
};

export const getProviderSearchNavigationHandlers = ({
  location,
  navigate,
  providerSearchContext,
}: {
  location: Location;
  navigate: NavigateFunction;
  providerSearchContext: ProviderSearchContext;
}) => {
  const urls = getProviderSearchNavigationUrls({
    location,
    providerSearchContext,
  });

  return {
    goToAccount({ accountId }: { accountId: number }) {
      navigate(urls.goToAccount({ accountId }));
    },
    goToConsultant({ consultantId }: { consultantId: number }) {
      navigate(urls.goToConsultant({ consultantId }));
    },
    goToGlossary(section?: GlossarySection) {
      navigate(urls.goToGlossary(section));
    },
    goToListing() {
      navigate(urls.goToListing());
    },
    goToOnboardingFunnel(searchString?: string) {
      navigate(urls.goToOnboardingFunnel(searchString));
    },
    goToProvider({
      careproviderId,
      zipcode,
    }: {
      careproviderId: number;
      zipcode?: string;
    }) {
      navigate(urls.goToProvider({ careproviderId, zipcode }));
    },
    goToRequest(id: number, tabName?: string) {
      navigate(urls.goToRequest(id, tabName));
    },
    goToSearchDashboard(id?: number) {
      navigate(urls.goToSearchDashboard(id));
    },
    goToTableOfContents() {
      navigate(urls.goToTableOfContents());
    },
  };
};
